import api from './api';

/**
 * Credit Memo Options — V1's `viewcreditmemooptions` / `viewcreditmemo`
 * (main.pl:19221, lib_invoice.pl:2307), reached from the Rebill/Credit modal.
 *
 * Deliberately the same five actions as Invoice Options and no more: V1 offers no
 * backup-paperwork picker on this path (`doCreditMemoOptions` is called without an
 * `$attach_files` argument) and no per-pro split confirmation. That is the shape the
 * dialog's `estimate` mode already has, so a credit memo reuses it.
 *
 * Every route is keyed on the RECORD the user opened, not on a credit memo number: the
 * number decides which records the memo covers and who it is addressed to, so the
 * server resolves it rather than trusting the client.
 */

export interface CreditMemoOptionsData {
  creditMemoNum: string;
  /** "<Sage tag>-<number>", the number printed on the document itself. */
  documentNumber: string;
  billto: number;
  billtoName: string;
  billtoEmails: string[];
  userEmail: string;
  total: string;
  /** How many rebill/credit records share this credit memo number. Usually 1; when it
   *  is more, the memo is ONE document covering all of them and the dialog says so. */
  recordCount: number;
  pronumbers: number[];
}

export interface CreditMemoEmailRequest {
  toSelf: boolean;
  toBillto: string[];
  toOther: string;
}

export interface CreditMemoEmailResult {
  creditMemoNum: string;
  subject: string;
  /** Who it actually went to — the sender alone outside production. */
  recipients: string[];
  /** Who it would go to in production. Same as `recipients` when not redirected. */
  intendedRecipients: string[];
  redirected: boolean;
  attachments: string[];
}

const base = (pronumber: number, recno: number) =>
  `/orders/${pronumber}/rebill-credit/${recno}/credit-memo`;

export async function loadCreditMemoOptions(
  pronumber: number,
  recno: number,
): Promise<CreditMemoOptionsData> {
  return (await api.get<CreditMemoOptionsData>(`${base(pronumber, recno)}/options`)).data;
}

/**
 * The credit memo PDF. A read — V1 drops the file in a temp directory and never records
 * it, so asking again re-renders from current data.
 *
 * `responseType: 'blob'` applies to the ERROR response too, so a 409 arrives with its
 * JSON body wrapped in a Blob and every failure would read "Request failed with status
 * code 409". Unwrap it so callers read `detail` as they do everywhere else — the same
 * guard `accountingInvoiceSelection.postForPdf` keeps.
 */
export async function renderCreditMemo(pronumber: number, recno: number): Promise<Blob> {
  try {
    return (await api.post<Blob>(`${base(pronumber, recno)}/render`, {}, {
      responseType: 'blob',
    })).data;
  } catch (error) {
    const response = (error as { response?: { data?: unknown } })?.response;
    if (response && response.data instanceof Blob) {
      try {
        response.data = JSON.parse(await response.data.text());
      } catch {
        // Not JSON — a gateway error page, say. Leave it; the status still surfaces.
      }
    }
    throw error;
  }
}

export async function emailCreditMemo(
  pronumber: number,
  recno: number,
  request: CreditMemoEmailRequest,
): Promise<CreditMemoEmailResult> {
  return (await api.post<CreditMemoEmailResult>(`${base(pronumber, recno)}/email`, request)).data;
}
